/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Collapsable
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       19th April 2025

*******************************************************************************************/
import React                    from 'react';
import { 
    useTheme, 
    Box, 
    alpha 
}                               from '@mui/material';
import ExpandMoreIcon           from '@mui/icons-material/ExpandMore';
import ExpandLessIcon           from '@mui/icons-material/ExpandLess';
import { Button }               from 'components';
import { useSize }              from 'hooks';

const noop      = () => {}

const ExpandButton = ({expanded = false, onClick : handleClick = noop, ...props}) => {
    const theme = useTheme();
    return (
        <Box align="right" mt = {1}>
            <Button 
                size    = "small" 
                color   = {
                    theme?.palette?.mode === 'light' 
                        ? "primary" 
                        : "secondary"
                }
                onClick = {handleClick} 
                endIcon = {
                    expanded 
                        ? <ExpandLessIcon /> 
                        : <ExpandMoreIcon />
                }
                {...props}
                sx      = {{
                    p               : 0.5,
                    lineHeight      : 'initial',
                    textTransform   : 'initial'
                    ,...props?.sx
                }}
            >
                {
                    expanded 
                        ? 'Show Less' 
                        : 'Show More'
                }
            </Button>
        </Box>
    )
};

export const Collapsible = ({ children, enabled = true, maxHeight = 100, zIndex = 1, background = undefined}) => {
    // Inside your component:
    const theme                                     = useTheme();
    const [expanded, setExpanded]                   = React.useState(false);
    const [shouldShowButton, setShouldShowButton]   = React.useState(false);
    const contentRef                                = React.useRef();

    const {height}                                  = useSize(contentRef)

    const handleToggleExpand = React.useCallback(() => {
        setExpanded(prev => !prev);
    },[])
  
    React.useEffect(() => {
        setShouldShowButton(
            Boolean(height > maxHeight)
        );
    }, [maxHeight,height]);
      
    return (
        <Box>
            <Box
                id = "collapsible"
                sx = {{
                    position    : 'relative',
                    ...(enabled && {
                        overflow    : 'hidden',
                        maxHeight   : expanded ? 'none' : maxHeight,
                        transition  : 'max-height 0.3s ease',
                    }),
                    background  : background || 'inherit'
                }}
            >
                    <Box ref = {contentRef} sx={{zIndex}}>
                        {children}
                    </Box>
        
                    {
                        enabled && shouldShowButton && !expanded && (
                        <Box
                            sx  = {{
                                zIndex          : zIndex + 1,
                                position        : 'absolute',
                                bottom          : 0,
                                left            : 0,
                                right           : 0,
                                top             : 0,
                                background      : `linear-gradient(
                                    to bottom,
                                    rgba(0, 0, 0, 0) 0%,
                                    ${alpha(background || theme.palette.background.paper, 0.50)} 50%,
                                    ${alpha(background || theme.palette.background.paper, 1.00)} 100%
                                )`
                            }}
                        >
                            <Box display="flex" flexDirection={"column"} height="100%" p={0.5} pr={0} sx={{zIndex : zIndex + 1}}>
                                <Box flexGrow={1}/>
                                <ExpandButton expanded={expanded} onClick={handleToggleExpand}/>
                            </Box>
                        </Box>
                    )}
                </Box>
        
                {
                    enabled && shouldShowButton && expanded && (
                    <ExpandButton expanded={expanded} onClick={handleToggleExpand}/>
                )}
        </Box>
    );
};

export default Collapsible;